import Pikaday from 'pikaday';
import keyCodes from 'a11y/lib/keyCodes';

export default class DatePicker {
  /**
   * Start the component.
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Bind class methods.
    this.ariaSetup = this.ariaSetup.bind(this);
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.togglePicker = this.togglePicker.bind(this);

    /**
     * ID for the button label when the calendar is closed.
     * @type {String}
     */
    this.openCalendar = 'open-calendar';

    /**
     * ID for the button label when the calendar is open.
     * @type {String}
     */
    this.selectDate = 'select-date';

    this.init();
  }

  /**
   * Pikaday `onOpen` callback.
   * Update the element's ARIA attributes.
   */
  onOpen() {
    const { trigger, container } = this.children;

    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-labelledby', this.selectDate);

    container.setAttribute('aria-hidden', 'false');
  }

  /**
   * Pikaday `onClose` callback.
   * Update the element's ARIA attributes.
   */
  onClose() {
    const { trigger, container } = this.children;

    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-labelledby', this.openCalendar);

    container.setAttribute('aria-hidden', 'true');
  }

  /**
   * Set up Pikaday event handling.
   * We've set Pikaday's `bound` option to false, so we need to handle a few things ourselves.
   */
  setEventListeners() {
    const { trigger } = this.children;
    const {
      TAB,
      ESC,
      LEFT,
      UP,
      RIGHT,
      DOWN,
    } = keyCodes;

    document.body.addEventListener('click', this.togglePicker);

    // Hide the date picker on outside tap.
    document.body.addEventListener('touchend', (event) => {
      const { target } = event;

      if (target !== trigger && this.picker.isVisible()) {
        this.picker.hide();
      }
    });

    document.body.addEventListener('keydown', (event) => {
      const { keyCode } = event;

      // Close the date picker on ESC-key down.
      if (this.picker.isVisible() && ESC === keyCode) {
        this.picker.hide();
      }

      // Prevent default for arrow keys while the date picker is open.
      if (
        [LEFT, UP, RIGHT, DOWN].includes(keyCode)
        && this.picker.isVisible()
      ) {
        event.preventDefault();
      }
    });

    trigger.addEventListener('keydown', (event) => {
      const { keyCode, shiftKey } = event;

      // Explicitly close the date picker on tab-key down, otherwise the focus is lost.
      if ((TAB === keyCode && ! shiftKey)) {
        this.picker.hide();
      }
    });
  }

  /**
   * Set ARIA attributes for the input and popup date picker.
   */
  ariaSetup() {
    const { trigger, container } = this.children;

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', container.id);
    trigger.removeAttribute('tabindex');

    container.setAttribute('aria-hidden', 'true');
  }

  /**
   * Toggle the date picker.
   *
   * @param  {Event} event The event object.
   */
  togglePicker(event) {
    const { trigger } = this.children;
    const { target } = event;

    if (target === trigger) {
      // On trigger click.
      if (this.picker.isVisible()) {
        this.picker.hide();
      } else {
        this.picker.show();
      }
    } else if (! this.picker.el.contains(target)) {
      // On outside click.
      this.picker.hide();
    }
  }

  /**
   * Set up and init Pikaday.
   */
  init() {
    const { field, trigger, container } = this.children;

    const pikadayOpts = {
      field,
      trigger,
      container,
      onOpen: () => {
        this.onOpen();
      },
      onClose: () => {
        this.onClose();
      },
      ...this.options,
    };

    this.ariaSetup();

    this.picker = new Pikaday(pikadayOpts);

    this.picker.hide();
    this.setEventListeners();
  }
}
